import { Component, OnInit, ViewChild, TemplateRef, AfterViewInit, HostListener, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { AdminSidebarComponent } from '../sidebar/admin-sidebar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { TableComponent, TableColumn, PaginationConfig, TabModalComponent, TabConfig } from '../../shared/components/primeng';
import { ActionDropdownTriggerDirective } from '../../shared/directives/action-dropdown-trigger.directive';
import { ConfirmationDialogComponent } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { FilterPanelComponent, FilterField } from '../../shared/components/filter-panel/filter-panel.component';
import { AuthApiService } from '@angular-workspace/services';
import { FormatService } from '../../shared/services/format.service';
import { ToasterService } from '../../services/toaster.service';

export interface UserData {
  id?: string;
  userName: string;
  organization: string;
  currentRole: string;
  accountStatus: string;
  userAddedOn: string;
  userId?: string;
  action?: string;
  originalData?: any; // Store original API response data
}

@Component({
  selector: 'app-admin-view-all-users',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    AdminSidebarComponent,
    PageHeaderComponent,
    TableComponent,
    TabModalComponent,
    ConfirmationDialogComponent,
    FilterPanelComponent,
    ActionDropdownTriggerDirective,
  ],
  templateUrl: './view-all-users.component.html',
  styleUrl: './view-all-users.component.scss',
})
export class AdminViewAllUsersComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('actionCell', { static: false }) actionCellTemplate!: TemplateRef<any>;
  @ViewChild('statusCell', { static: false }) statusCellTemplate!: TemplateRef<any>;
  @ViewChild('userTabContent', { static: false }) userTabContentTemplate!: TemplateRef<any>;
  @ViewChild('organisationTabContent', { static: false }) organisationTabContentTemplate!: TemplateRef<any>;

  openDropdownId: string | null = null;
  isModalVisible = false;
  selectedUser: UserData | null = null;
  userDetails: any = null; // Store user details from API
  modalTabs: TabConfig[] = [];
  
  // Confirmation dialog state
  isConfirmationVisible = false;
  userToDeactivate: UserData | null = null;
  isActivating = false; // Track if we're activating or deactivating

  constructor(
    private authApiService: AuthApiService,
    public formatService: FormatService, // Made public for template access
    private toasterService: ToasterService
  ) {}

  columns: TableColumn[] = [
    { key: 'userName', label: 'User Name', sortable: false, width: '200px' },
    { key: 'organization', label: 'Organization', sortable: false, width: '20%' },
    { key: 'currentRole', label: 'Current Role', sortable: false, width: '15%' },
    { key: 'accountStatus', label: 'Account Status', sortable: false, width: '15%' },
    { key: 'userAddedOn', label: 'User Added On', sortable: false, width: '15%' },
    { key: 'action', label: 'Action', sortable: false, width: '15%', align: 'left', fixed: 'right' }
  ];

  usersData: UserData[] = [];

  pagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: true
  };

  loading = false;
  emptyMessage = 'No users found';

  // Filter fields
  filterFields: FilterField[] = [
    {
      key: 'search',
      label: 'Search',
      type: 'text',
      placeholder: 'Search by user name or email',
      value: ''
    }
  ];

  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;
  searchTerm: string = '';

  ngOnInit(): void {
    // Set up debounced search subscription
    this.searchSubscription = this.searchSubject
      .pipe(
        debounceTime(500), // Wait 500ms after user stops typing
        distinctUntilChanged() // Only trigger if search term actually changed
      )
      .subscribe(searchTerm => {
        this.searchTerm = searchTerm;
        this.pagination.currentPage = 1; // Reset to first page on new search
        this.loadUsers();
      });
    
    this.loadUsers();
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  async loadUsers(): Promise<void> {
    this.loading = true;
    try {
      // Only pass search term if it's 3 or more characters
      const searchParam = this.searchTerm && this.searchTerm.trim().length >= 3 ? this.searchTerm.trim() : undefined;
      
      const response = await this.authApiService.getAllPlatformUsers(
        this.pagination.currentPage,
        this.pagination.pageSize,
        searchParam
      );

      let mappedData: UserData[] = [];

      // Handle API response structure: { result: [...], paginationInfo: {...} }
      // First, update pagination from paginationInfo if it exists (even if result is empty or missing)
      if (response && response['paginationInfo'] && response['paginationInfo']['totalCount'] !== undefined) {
        this.pagination = {
          ...this.pagination,
          totalItems: response['paginationInfo']['totalCount']
        };
      }
      
      if (response && response['result'] && Array.isArray(response['result'])) {
        const dataArray = response['result'];
        mappedData = dataArray.map((item: any) => {
          // Extract organization name from nested object
          const orgName = item.organisation?.name || item.organization?.name || item.orgName || item.organizationName || item.organisationName || '';
          const rawRole = item.currentRole || item.role || item.roles?.[0] || '';
          
          // Map account_enabled to accountStatus
          // account_enabled: true -> "Active", account_enabled: false -> "Inactive"
          const accountEnabled = item.account_enabled !== undefined ? item.account_enabled : (item.accountEnabled !== undefined ? item.accountEnabled : true);
          const accountStatus = accountEnabled ? 'Active' : 'Inactive';
          
          return {
            id: item.sub || item.id || item.userId || JSON.stringify(item),
            userId: item.sub || item.userId || item.id || item.user_id,
            userName: item.userName || item.user_name || item.name || item.email || item.preferredUsername || 'Unknown User',
            organization: orgName || '-',
            currentRole: this.formatService.toPascalCase(rawRole),
            accountStatus: accountStatus,
            userAddedOn: this.formatDate(item.userAddedOn || item.user_added_on || item.createdAt || item.created_at || item.createdDate || item.created_date || ''),
            action: 'Action',
            originalData: item // Store original API response data
          };
        });
      } else if (response && response['data'] && Array.isArray(response['data'])) {
        // Fallback: Handle legacy response structure with 'data' property
        const dataArray = response['data'];
        mappedData = dataArray.map((item: any) => {
          // Extract organization name from nested object
          const orgName = item.organisation?.name || item.organization?.name || item.orgName || item.organizationName || item.organisationName || '';
          const rawRole = item.currentRole || item.role || item.roles?.[0] || '';
          
          // Map account_enabled to accountStatus
          // account_enabled: true -> "Active", account_enabled: false -> "Inactive"
          const accountEnabled = item.account_enabled !== undefined ? item.account_enabled : (item.accountEnabled !== undefined ? item.accountEnabled : true);
          const accountStatus = accountEnabled ? 'Active' : 'Inactive';
          
          return {
            id: item.sub || item.id || item.userId || JSON.stringify(item),
            userId: item.sub || item.userId || item.id || item.user_id,
            userName: item.userName || item.user_name || item.name || item.email || item.preferredUsername || 'Unknown User',
            organization: orgName || '-',
            currentRole: this.formatService.toPascalCase(rawRole),
            accountStatus: accountStatus,
            userAddedOn: this.formatDate(item.userAddedOn || item.user_added_on || item.createdAt || item.created_at || item.createdDate || item.created_date || ''),
            action: 'Action',
            originalData: item // Store original API response data
          };
        });

        if (response['total'] !== undefined) {
          this.pagination = { ...this.pagination, totalItems: response['total'] };
        } else if (response['count'] !== undefined) {
          this.pagination = { ...this.pagination, totalItems: response['count'] };
        }
      } else if (Array.isArray(response)) {
        // Fallback: Handle direct array response
        mappedData = response.map((item: any) => {
          // Extract organization name from nested object
          const orgName = item.organisation?.name || item.organization?.name || item.orgName || item.organizationName || item.organisationName || '';
          const rawRole = item.currentRole || item.role || item.roles?.[0] || '';
          
          // Map account_enabled to accountStatus
          // account_enabled: true -> "Active", account_enabled: false -> "Inactive"
          const accountEnabled = item.account_enabled !== undefined ? item.account_enabled : (item.accountEnabled !== undefined ? item.accountEnabled : true);
          const accountStatus = accountEnabled ? 'Active' : 'Inactive';
          
          return {
            id: item.sub || item.id || item.userId || JSON.stringify(item),
            userId: item.sub || item.userId || item.id || item.user_id,
            userName: item.userName || item.user_name || item.name || item.email || item.preferredUsername || 'Unknown User',
            organization: orgName || '-',
            currentRole: this.formatService.toPascalCase(rawRole),
            accountStatus: accountStatus,
            userAddedOn: this.formatDate(item.userAddedOn || item.user_added_on || item.createdAt || item.created_at || item.createdDate || item.created_date || ''),
            action: 'Action',
            originalData: item // Store original API response data
          };
        });
        this.pagination = { ...this.pagination, totalItems: mappedData.length };
      } else if (response && response['paginationInfo']) {
        // Handle case where response has paginationInfo but no result/data array
        // This can happen when totalCount is 0
        if (response['paginationInfo']['totalCount'] !== undefined) {
          this.pagination = {
            ...this.pagination,
            totalItems: response['paginationInfo']['totalCount']
          };
        }
        mappedData = []; // No data to map
      }

      this.usersData = mappedData;

      if (mappedData.length === 0) {
        this.emptyMessage = 'No users found';
      }
    } catch (error) {
      console.error('Error loading users:', error);
      this.usersData = [];
      this.pagination = { ...this.pagination, totalItems: 0 };
      this.emptyMessage = 'No users found';
    } finally {
      this.loading = false;
    }
  }

  onPageChange(page: number): void {
    this.pagination = { ...this.pagination, currentPage: page };
    this.loadUsers();
  }

  onPageSizeChange(pageSize: number): void {
    this.pagination = { ...this.pagination, pageSize: pageSize, currentPage: 1 };
    this.loadUsers();
  }


  formatDate(dateString: string): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'short' });
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day}-${month}-${year} ${hours}:${minutes}`;
    } catch (error) {
      return dateString;
    }
  }

  ngAfterViewInit(): void {
    // Assign action cell template to the action column
    setTimeout(() => {
      const actionColumn = this.columns.find(col => col.key === 'action');
      if (actionColumn && this.actionCellTemplate) {
        actionColumn.cellTemplate = this.actionCellTemplate;
      }

      // Assign status cell template to the accountStatus column
      const statusColumn = this.columns.find(col => col.key === 'accountStatus');
      if (statusColumn && this.statusCellTemplate) {
        statusColumn.cellTemplate = this.statusCellTemplate;
      }

      // Initialize modal tabs
      this.initializeModalTabs();
    }, 0);
  }

  toggleActionDropdown(event: Event, row: UserData): void {
    event.stopPropagation();
    const rowId = row.id || JSON.stringify(row);
    if (this.openDropdownId === rowId) {
      this.openDropdownId = null;
      this.dropdownPosition = null;
      return;
    }
    const btn = event.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    this.dropdownPosition = {
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width
    };
    this.openDropdownId = rowId;
  }

  closeActionDropdown(): void {
    this.openDropdownId = null;
    this.dropdownPosition = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.openDropdownId !== null &&
        !target.closest('.action-cell-wrapper') &&
        !target.closest('.action-dropdown-overlay')) {
      this.closeActionDropdown();
    }
  }

  /** Row whose action dropdown is open; used by the fixed overlay. */
  get openDropdownRow(): UserData | null {
    if (this.openDropdownId == null) return null;
    return this.usersData.find(
      (r) => (r?.id ?? JSON.stringify(r)) === this.openDropdownId
    ) ?? null;
  }

  /** Position for the fixed dropdown overlay (so it appears on top of the table). */
  dropdownPosition: { top: number; left: number; width: number } | null = null;

  initializeModalTabs(): void {
    if (this.userTabContentTemplate && this.organisationTabContentTemplate) {
      this.modalTabs = [
        {
          key: 'user',
          label: 'User',
          contentTemplate: this.userTabContentTemplate
        },
        {
          key: 'organisation',
          label: 'Organisation Details',
          contentTemplate: this.organisationTabContentTemplate
        }
      ];
    }
  }

  handleAction(action: string, row: UserData): void {
    if (action === 'view') {
      // Use the original API response data stored in the row
      this.selectedUser = row;
      this.userDetails = row.originalData || null;
      
      // Reinitialize tabs with updated templates
      this.initializeModalTabs();
      
      this.isModalVisible = true;
    } else if (action === 'deactivate' || action === 'activate') {
      // Store the user and action type, then show confirmation modal
      this.userToDeactivate = row;
      this.isActivating = action === 'activate';
      this.isConfirmationVisible = true;
    }
    this.closeActionDropdown();
  }

  onFilterChange(filters: Record<string, any>): void {
    // Update filter field values
    this.filterFields.forEach((field: FilterField) => {
      if (filters[field.key] !== undefined) {
        field.value = filters[field.key];
      }
    });
    
    // Get search value from filters
    const searchValue = filters['search'] || '';
    
    // Only trigger API call if search term is 3 or more characters, or if it's empty (to clear search)
    if (searchValue.trim().length >= 3 || searchValue.trim().length === 0) {
      this.searchSubject.next(searchValue);
    }
  }

  onClearFilters(): void {
    // Reset filter field values
    this.filterFields.forEach((field: FilterField) => {
      field.value = field.type === 'text' ? '' : field.type === 'multiselect' ? [] : field.type === 'dateRange' ? null : '';
    });
    
    // Clear search and reload all users
    this.searchTerm = '';
    this.pagination.currentPage = 1;
    this.loadUsers();
  }

  async onDeactivateConfirm(): Promise<void> {
    // Close the modal immediately
    this.isConfirmationVisible = false;
    const userToUpdate = this.userToDeactivate;
    const isActivating = this.isActivating;
    
    // Reset state
    this.userToDeactivate = null;
    this.isActivating = false;
    
    if (userToUpdate) {
      try {
        // Get the sub field from originalData or userId
        const userId = userToUpdate.originalData?.sub || userToUpdate.userId || userToUpdate.id;
        
        if (!userId) {
          console.error('User ID (sub) not found');
          return;
        }
        
        // Prepare update data based on action (activate or deactivate)
        // The API expects status: "activate" or status: "deactivate"
        const updateData = {
          status: isActivating ? 'activate' : 'deactivate'
        };
        
        // Call the deactivate/activate user API
        await this.authApiService.deactivateUser(userId, updateData);
        
        // Show success toaster
        const userName = userToUpdate.userName || 'the user';
        if (isActivating) {
          this.toasterService.success(`You've successfully activated ${userName}`);
        } else {
          this.toasterService.success(`You've successfully deactivated ${userName}`);
        }
        
        // Reload users after successful update
        await this.loadUsers();
      } catch (error: any) {
        console.error(`Error ${isActivating ? 'activating' : 'deactivating'} user:`, error);
        // Show error toaster
        const errorMessage = error?.error?.detail || error?.message || `Failed to ${isActivating ? 'activate' : 'deactivate'} user. Please try again.`;
        this.toasterService.error(errorMessage);
      }
    }
  }

  onDeactivateCancel(): void {
    this.isConfirmationVisible = false;
    this.userToDeactivate = null;
    this.isActivating = false;
  }

  closeModal(): void {
    this.isModalVisible = false;
    this.selectedUser = null;
    this.userDetails = null;
  }

  onModalClose(): void {
    this.closeModal();
  }

  isDropdownOpen(row: UserData): boolean {
    const rowId = row.id || JSON.stringify(row);
    return this.openDropdownId === rowId;
  }
}


